import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { BrowserRouter } from "react-router-dom";
import { datadogRum } from "@datadog/browser-rum";
import { LDProvider, LDContext } from "launchdarkly-react-client-sdk";
import styled from "styled-components/macro";
import AppContainer from "./AppContainer";
import "./App.css";
import { AUTH_CALLBACK_URL, LOGIN_PAGE_URL } from "./utils/urlConstants";
import Loading from "./components/shared/Loading/Loading";
import { RefreshProviderInfo } from "./components/CreateAccount/Services/CreateAccountService";
import { RefreshProviderProfile } from "./components/Profile/Services/UpdateProfileInfoService";
import {
  defaultReqsRequestComplete,
  defaultRequisitions,
  providerProfileState,
  providerState,
} from "./recoil/atoms";
import { useAuthenticationHook } from "./utils/hooks/useAuthenticationHook";
import { getBaseUrl, getCookieAccessToken } from "./utils/requestUtils";
import { SetAnalyticsUser, TrackAnalyticsEvent } from "./services/Analytics";
import { LAUNCHDARKLY_CLIENT_ID } from "./utils/launchDarklyUtils";
import {
  getSanitizedProviderData,
  getDetailedProviderData,
} from "./utils/contextUtils";
import {
  checkAuth0DomainConnectivity,
  isAuth0ConnectivityCheckConfigured,
} from "./auth/auth0ConnectivityCheck";

export const Container = styled.div`
  position: relative;
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
  background: ${({ theme }) => theme.neutral50};
  overflow-x: clip;
`;

const App = (): React.ReactElement => {
  const isLocalEnv = import.meta.env.VITE_ENV === "local";
  const { setLoggedOut } = useAuthenticationHook();
  const [isLoading, setIsLoading] = useState(false);
  const [providerInfo, setProviderInfo] = useRecoilState(providerState);
  const [profileInfo, setProfileInfo] = useRecoilState(providerProfileState);
  const [, setRequisitions] = useRecoilState(defaultRequisitions);
  const [, setDefaultReqComplete] = useRecoilState(defaultReqsRequestComplete);
  const [ldContext, setLdContext] = useState<LDContext>({
    kind: "user",
    key: "anonymous",
    anonymous: true,
  });
  const { pathname } = window.location;

  const trackBaseUrl = async () => {
    if (!providerInfo) {
      return;
    }
    const baseUrl = await getBaseUrl();
    TrackAnalyticsEvent("ProviderBaseUrl", providerInfo.token, undefined, {
      baseUrl,
    });
  };

  useEffect(() => {
    if (providerInfo) {
      trackBaseUrl();
      datadogRum.setUser({
        id: providerInfo.username,
        email: providerInfo.email,
      });
    }
  }, [providerInfo]);

  useEffect(() => {
    if (providerInfo && profileInfo) {
      SetAnalyticsUser(providerInfo, profileInfo, providerInfo.username);

      const context = {
        kind: "user",
        key: providerInfo.username,
        ...getSanitizedProviderData(providerInfo),
        ...getDetailedProviderData(profileInfo),
      };
      setLdContext(context);

      if (isAuth0ConnectivityCheckConfigured()) {
        checkAuth0DomainConnectivity().then(({ domain, reachable }) => {
          TrackAnalyticsEvent(
            "UniversalLoginConnectivityCheck",
            providerInfo.token,
            undefined,
            {
              sf_account_id: profileInfo.account.sfid,
              sf_account_name: profileInfo.account.sfname,
              domain,
              reachable: reachable ? 1 : 0,
            },
          );
        });
      }
    }
  }, [providerInfo, profileInfo]);

  useEffect(() => {
    if (pathname.startsWith(LOGIN_PAGE_URL) || pathname === AUTH_CALLBACK_URL) {
      return;
    }

    const authToken = getCookieAccessToken();
    (async () => {
      if (authToken) {
        setIsLoading(true);
        await RefreshProviderInfo(
          authToken,
          setProviderInfo,
          setRequisitions,
          setDefaultReqComplete,
          setLoggedOut,
        );
        setIsLoading(false);

        // Also get profile info for analytics.
        // Not necessary for the page load, so we do it after setting loading to false
        await RefreshProviderProfile(authToken, setProfileInfo, setLoggedOut);
      }
    })();
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container className="App">
      <LDProvider
        clientSideID={LAUNCHDARKLY_CLIENT_ID}
        context={ldContext}
        deferInitialization
        options={{ bootstrap: "localStorage", sendEvents: !isLocalEnv }}
      >
        <BrowserRouter>
          <AppContainer />
        </BrowserRouter>
      </LDProvider>
    </Container>
  );
};

export default App;
